import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "inputRef", "value", "label", "labelProps", "isSearching", "withBorder", "transparent", "onSubmit", "onClear", "wrapperRef", "wrapperProps"];
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.search.js";
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { pxToRem } from 'utils/ui/units';
import { alpha } from 'utils/ui/colors';
import { spacing } from 'styles/globals';
import { textInputReset } from 'components/TextInput';
import TooltipButton from 'components/TooltipButton';
import VisuallyHidden from 'components/VisuallyHidden';
import useUniqueId from 'hooks/useUniqueId';
import useMergedRefs from 'hooks/useMergedRefs';
var SEARCH_BOX_HEIGHT = pxToRem(40);
var Wrapper = styled.div.withConfig({
  displayName: "SearchBox__Wrapper",
  componentId: "sc-8k5vaw-0"
})(["display:flex;height:", ";margin:0;padding:0;padding-left:", ";align-items:center;background-color:", ";border:1px solid transparent;border-radius:", ";cursor:text;transition:all 0.25s ease-in;&:has(input:focus){border-color:", ";box-shadow:0 0 0 1px ", ";transition-timing-function:ease-out;}", " ", ""], SEARCH_BOX_HEIGHT, spacing.xs, function (p) {
  return alpha(p.theme.shade, p.theme.shadeStrength);
}, function (p) {
  return pxToRem(p.theme.globals.borderRadius.search);
}, function (p) {
  return p.theme.links;
}, function (p) {
  return p.theme.links;
}, function (p) {
  return p.withBorder && css(["border-color:", ";"], function (p) {
    return alpha(p.theme.shade, p.theme.lineStrength);
  });
}, function (p) {
  return p.transparent && css(["background-color:transparent;border-color:transparent;"]);
});
var InputWrapper = styled.div.withConfig({
  displayName: "SearchBox__InputWrapper",
  componentId: "sc-8k5vaw-1"
})(["flex:1 1 auto;"]);
var Input = styled.input.withConfig({
  displayName: "SearchBox__Input",
  componentId: "sc-8k5vaw-2"
})(["", " width:100%;padding:0 ", ";color:", ";&::placeholder{color:", ";}&::-ms-clear,&::-ms-reveal{display:none;width:0;height:0;}"], textInputReset, spacing.xs, function (p) {
  return p.theme.text;
}, function (p) {
  return alpha(p.theme.text, p.theme.textDimStrength);
});
export var SearchShapedButton = styled(TooltipButton).withConfig({
  displayName: "SearchBox__SearchShapedButton",
  componentId: "sc-8k5vaw-3"
})(["", " border-radius:", ";"], function (p) {
  return (!p.size || p.size === 'default') && "\n\t\tmin-height: auto;\n\t\theight: ".concat(SEARCH_BOX_HEIGHT, ";\n\t\t");
}, function (p) {
  return pxToRem(p.theme.globals.borderRadius.search);
});
function SearchBox(_ref) {
  var id = _ref.id,
    inputRefProp = _ref.inputRef,
    value = _ref.value,
    label = _ref.label,
    labelProps = _ref.labelProps,
    isSearching = _ref.isSearching,
    withBorder = _ref.withBorder,
    transparent = _ref.transparent,
    onSubmit = _ref.onSubmit,
    onClear = _ref.onClear,
    wrapperRef = _ref.wrapperRef,
    wrapperProps = _ref.wrapperProps,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var uniqueId = useUniqueId(id);
  var inputRef = useRef(null);
  var inputRefs = useMergedRefs([inputRef, inputRefProp]);
  function focusInput(e) {
    if (e.currentTarget === e.target) {
      var _inputRef$current;
      e.preventDefault();
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
    }
  }
  function submitSearch(e) {
    if (onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  }
  var hasSearchText = !!value;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(VisuallyHidden, _extends({
    as: "label",
    htmlFor: uniqueId
  }, labelProps), label || t('actions_search')), /*#__PURE__*/React.createElement(Wrapper, {
    ref: wrapperRef,
    as: onSubmit ? 'form' : 'div',
    onSubmit: submitSearch,
    transparent: transparent,
    withBorder: withBorder,
    onMouseDown: focusInput
  }, /*#__PURE__*/React.createElement(InputWrapper, null, /*#__PURE__*/React.createElement(Input, _extends({
    type: "text",
    id: uniqueId,
    ref: inputRefs,
    autoComplete: "off",
    value: value || ''
  }, otherProps))), onClear && hasSearchText && /*#__PURE__*/React.createElement(SearchShapedButton, {
    iconOnly: true,
    icon: "x",
    size: "medium",
    variant: "transparent",
    onClick: function onClick() {
      return onClear(inputRef);
    },
    mt: 1
  }, t('actions_clear_search')), /*#__PURE__*/React.createElement(SearchShapedButton, {
    iconOnly: true,
    iconRight: "search",
    iconScale: 1.5,
    variant: hasSearchText ? 'primary' : 'transparent',
    isLoading: isSearching,
    type: "submit",
    ml: "xxxs"
  }, t('actions_search'))));
}
export default SearchBox;